<template>
  <transition name="rm-pop" appear>
    <div v-if="localVisible" class="rm-overlay" @mousedown.self="close">
      <div
        class="rm-dialog rm-dialog--hotel"
        :class="{ 'rm-dialog--mobile': $isMobile }"
        role="dialog"
        aria-modal="true"
      >
        <LoaderModal
          :message="tt('modal.processing', 'Procesando...')"
          v-if="loaderLocal"
        />

        <!-- Header -->
        <header class="rm-head">
          <div class="rm-head__icon"><i class="fal fa-calendar-plus"></i></div>
          <div class="rm-head__tx">
            <h2 class="rm-head__title">
              {{
                isEditMode
                  ? tt("calendarHotel.modal.editTitle", "Editar reserva")
                  : tt("calendarHotel.modal.title", "Nueva reserva")
              }}
            </h2>
            <p v-if="form.roomType && form.roomType.name" class="rm-head__sub">
              {{ form.roomType.name }}
            </p>
          </div>
          <button
            type="button"
            class="rm-head__close"
            @click="close"
            :aria-label="$t('reservasHc.hotelResModal.close')"
          >
            <i class="fal fa-times"></i>
          </button>
        </header>

        <!-- Body -->
        <div class="rm-body">
          <!-- Banner OTA -->
          <div v-if="otaLocked" class="rm-alert rm-alert--danger" role="alert">
            <i class="fas fa-exclamation-circle"></i>
            <span v-html="$t('reservasHc.hotelResModal.otaBanner')"></span>
          </div>

          <!-- Fechas -->
          <div class="rm-grid-2">
            <div class="rm-field" style="grid-column: 1 / -1">
              <label class="rm-label">
                {{ tt("calendarHotel.modal.startDate", "Entrada") }} → {{ tt("calendarHotel.modal.endDate", "Salida") }}
                <span class="rm-req">*</span>
              </label>
              <RangoDeFechas contorno :desde="form.startDate" :hasta="form.endDate" :min="minDate" :min-dias="1" :disabled="otaLocked" bloque @change="form.startDate = $event.from; form.endDate = $event.to" />
            </div>
          </div>

          <div v-if="staySummaryText" class="rm-nights">
            <i class="fal fa-moon"></i>
            <span>{{ staySummaryText }}</span>
          </div>

          <p v-if="otaLocked" class="rm-lock">
            <i class="fal fa-lock"></i>
            {{ $t("reservasHc.hotelResModal.otaLock") }}
          </p>

          <!-- (Habitaciones y precio se movió tras los datos del huésped, al final) -->

          <!-- Nombre -->
          <div class="rm-field">
            <label class="rm-label">
              {{ tt("calendarHotel.modal.guestName", "Nombre huésped") }}
              <span class="rm-req">*</span>
            </label>
            <div ref="guestAutocompleteWrap" class="rm-guest">
              <div class="rm-guest__control">
                <input
                  v-model="form.name"
                  class="rm-input"
                  autocomplete="off"
                  :placeholder="tt('calendarHotel.modal.guestNamePlaceholder', 'Nombre del huésped')"
                  @input="selectedGuestSuggestionId = null"
                />
                <button
                  v-if="!isEditMode"
                  type="button"
                  class="rm-guest__btn"
                  :class="{ 'rm-guest__btn--on': showGuestSuggestions || selectedGuestSuggestionId }"
                  :title="$t('clientesDir.picker.searchSaved')"
                  :aria-label="$t('clientesDir.picker.searchSaved')"
                  :aria-expanded="String(showGuestSuggestions)"
                  @click="toggleGuestPicker"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="10" cy="8" r="4" />
                    <path d="M3 20c0-3.3 3-6 7-6 1 0 1.9.2 2.8.5" />
                    <circle cx="17.5" cy="16.5" r="3" />
                    <path d="m21.5 20.5-1.8-1.8" />
                  </svg>
                </button>
              </div>
              <div v-if="showGuestSuggestions" class="rm-guest__list">
                <div class="rm-guest__search">
                  <input
                    ref="guestPickerInput"
                    v-model="guestPickerQuery"
                    type="text"
                    class="rm-input"
                    autocomplete="off"
                    :placeholder="$t('clientesDir.picker.searchPlaceholder')"
                    @input="searchGuests"
                    @keydown.esc.stop.prevent="showGuestSuggestions = false"
                  />
                </div>
                <div class="rm-guest__results">
                  <div
                    v-if="guestSearchLoading && !guestSuggestions.length"
                    class="rm-guest__item rm-guest__item--loading"
                  >
                    {{ $t("bookingsHc.rerservationModal.searchingGuests") }}
                  </div>
                  <div
                    v-else-if="!guestSuggestions.length"
                    class="rm-guest__item rm-guest__item--loading"
                  >
                    {{ $t("clientesDir.picker.noResults") }}
                  </div>
                  <button
                    v-for="guest in guestSuggestions"
                    :key="guest.id"
                    type="button"
                    class="rm-guest__item"
                    @click="applyGuestSuggestion(guest)"
                  >
                    <div class="rm-guest__main">{{ guest.nombre_completo || $t("bookingsHc.rerservationModal.noName") }}</div>
                    <div class="rm-guest__meta">
                      <span v-if="guest.nu_documento">{{ guest.nu_documento }}</span>
                      <span v-if="guest.email">{{ guest.email }}</span>
                      <span v-if="guest.telefono">{{ guest.telefono }}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Nº huéspedes -->
          <div class="rm-field">
            <label class="rm-label">
              {{ tt("calendarHotel.modal.guests", "Nº de huéspedes") }}
              <span class="rm-req">*</span>
            </label>
            <input
              type="number"
              v-model="form.number_of_guests"
              :min="1"
              class="rm-input"
              :placeholder="tt('calendarHotel.modal.guestsPlaceholder', 'Ej: 2')"
            />
          </div>

          <!-- Teléfono -->
          <div class="rm-field">
            <label class="rm-label">
              {{ tt("calendarHotel.modal.phone", "Teléfono") }}
              <span class="rm-req">*</span>
            </label>
            <vue-tel-input
              v-model="form.phone"
              class="rm-tel"
              :autoDefaultCountry="true"
              :inputOptions="{ showDialCode: true }"
              @input="alCambiarTelefono"
            />
          </div>

          <!-- Email -->
          <div class="rm-field">
            <label class="rm-label">{{ tt("calendarHotel.modal.email", "Email") }}</label>
            <input
              type="email"
              v-model="form.email"
              class="rm-input"
              :placeholder="$t('reservasHc.hotelResModal.placeholderEmail')"
            />
          </div>

          <!-- Observación -->
          <div class="rm-field">
            <label class="rm-label">{{ tt("calendarHotel.modal.notes", "Observación") }}</label>
            <textarea
              v-model="form.observacion"
              class="rm-input rm-textarea"
              rows="3"
              maxlength="2000"
              :placeholder="tt('calendarHotel.modal.notesPlaceholder', 'Notas internas de la reserva (ej. llega tarde, cuna, alergias…)')"
            ></textarea>
          </div>

          <!-- Habitaciones y precio (al final): Tipo · Cantidad · Precio/noche + Total -->
          <div class="rm-field">
            <label class="rm-label">
              {{ tt("calendarHotel.modal.roomsPrice", "Habitaciones y precio") }}
              <span class="rm-req">*</span>
              <span v-if="form.roomType" class="rm-label__hint">
                · {{ tt("calendarHotel.modal.available", "Disponibles") }}
                ({{ availableInstancesLabel }})
              </span>
            </label>

            <div class="rm-rooms">
              <!-- Cabecera de columnas -->
              <div v-if="form.roomType" class="rm-rooms__cols">
                <span class="rmc rmc--type">{{ tt("calendarHotel.modal.roomType", "Tipo de habitación") }}</span>
                <span class="rmc rmc--qty">{{ tt("calendarHotel.modal.qtyShort", "Cant.") }}</span>
                <span class="rmc rmc--price">{{ tt("calendarHotel.modal.pricePerNight", "Precio / noche") }}</span>
                <span class="rmc rmc--x"></span>
              </div>

              <!-- Fila del tipo principal (con etiqueta de la habitación del calendario) -->
              <div class="rm-room rm-room--main" :style="primaryMarkedForRemoval ? 'opacity:.55' : ''">
                <div class="rm-room__type">
                  <transition name="rm-preset">
                    <span v-if="activePresetInstance" class="rm-room__preset">
                      <i class="fa-solid fa-bed"></i>
                      {{ $t("reservasHc.hotelResModal.roomAbbr") }} <strong>{{ activePresetInstance.codigo }}</strong>
                      <span class="rm-room__preset-sub">· {{ $t("reservasHc.hotelResModal.fromCalendar") }}</span>
                    </span>
                  </transition>
                  <multiselect
                    :value="form.roomType"
                    :options="roomTypes"
                    :custom-label="(o) => o.name"
                    track-by="id"
                    :searchable="true"
                    :show-labels="false"
                    :allow-empty="false"
                    :max-height="240"
                    open-direction="top"
                    class="rm-ms"
                    :disabled="typeQtyLocked"
                    :placeholder="tt('calendarHotel.modal.roomTypePlaceholder', 'Selecciona tipo')"
                    @input="onRoomTypePicked"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <i class="fal fa-bed rm-ms__ic"></i> {{ option.name }}
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <i class="fal fa-bed rm-ms__ic"></i> {{ option.name }}
                    </template>
                    <template slot="noResult"><span class="rm-ms__nores">{{ $t("reservasHc.hotelResModal.noMatches") }}</span></template>
                  </multiselect>
                </div>
                <div class="rm-room__qty">
                  <input
                    type="number"
                    class="rm-input rm-input--c"
                    v-model.number="form.quantity"
                    min="1"
                    :max="maxQuantityAllowed"
                    :disabled="qtyLocked"
                    @input="clampMainQty"
                  />
                </div>
                <div v-if="!ocultaImportes" class="rm-room__price">
                  <span class="rm-cur">{{ currencySymbol }}</span>
                  <input
                    type="number"
                    class="rm-input rm-input--c"
                    v-model.number="form.pricePerNight"
                    min="0"
                    step="0.01"
                    :disabled="otaLocked"
                    @input="onPrimaryPerNightManual"
                  />
                  <button
                    v-if="!otaLocked && hasComputedPrice && isManualPrice"
                    type="button"
                    class="rm-room__auto"
                    :title="tt('calendarHotel.modal.useAutoPrice', 'Usar precio automático')"
                    @click="applyAutoPrice"
                  ><i class="fal fa-sync"></i></button>
                </div>
                <button
                  v-if="isEditMode && editGroupLines.length"
                  type="button"
                  class="rm-room__x rm-room__x--btn"
                  :title="primaryMarkedForRemoval ? $t('reservasHc.hotelResModal.undo') : $t('reservasHc.hotelResModal.removeRoom')"
                  @click="primaryMarkedForRemoval = !primaryMarkedForRemoval"
                >
                  <i :class="primaryMarkedForRemoval ? 'fal fa-undo' : 'fal fa-times'"></i>
                </button>
                <span v-else class="rm-room__x"></span>
              </div>
              <transition name="rm-note">
                <p v-if="primaryMarkedForRemoval" class="rm-room__note" style="color:#e5484d">
                  {{ $t("reservasHc.hotelResModal.willBeRemovedOnSave") }}
                </p>
              </transition>

              <!-- EDICIÓN de reserva COMBINADA: las OTRAS habitaciones del grupo (mismo
                   codigo_reserva), EDITABLES en cantidad (igual que al crear). Es UNA sola
                   reserva: los datos del huésped se comparten. Una habitación con 0
                   disponibilidad no se puede aumentar ni suma al total. -->
              <template v-if="isEditMode && editGroupLines.length">
                <div class="rm-rooms__extras">
                  <div v-for="line in editGroupLines" :key="line.reservaId" class="rm-xrow">
                    <div class="rm-room rm-room--extra" :style="line.markedForRemoval ? 'opacity:.55' : ''">
                      <div class="rm-room__type">
                        <div class="rm-select-wrap">
                          <input
                            class="rm-input rm-select"
                            :style="line.markedForRemoval ? 'text-decoration:line-through' : ''"
                            :value="line.unitName"
                            disabled
                          />
                        </div>
                      </div>
                      <div class="rm-room__qty">
                        <input
                          type="number"
                          class="rm-input rm-input--c"
                          min="1"
                          :max="line.available != null ? line.available : undefined"
                          :disabled="line.markedForRemoval"
                          v-model.number="line.quantity"
                          @input="onEditGroupQtyChange(line)"
                        />
                      </div>
                      <div v-if="!ocultaImportes" class="rm-room__price">
                        <span class="rm-cur">{{ currencySymbol }}</span>
                        <input
                          type="number"
                          class="rm-input rm-input--c"
                          min="0"
                          step="0.01"
                          :disabled="line.markedForRemoval"
                          v-model.number="line.pricePerNight"
                          @input="onEditGroupPriceChange(line)"
                        />
                      </div>
                      <button
                        type="button"
                        class="rm-room__x rm-room__x--btn"
                        :title="line.markedForRemoval ? $t('reservasHc.hotelResModal.undo') : $t('reservasHc.hotelResModal.removeRoom')"
                        @click="toggleEditGroupRemoval(line)"
                      >
                        <i :class="line.markedForRemoval ? 'fal fa-undo' : 'fal fa-times'"></i>
                      </button>
                    </div>
                    <transition name="rm-note">
                      <p
                        v-if="line.markedForRemoval || line.checking || line.available != null"
                        class="rm-room__note"
                        :style="(line.markedForRemoval || isLineBlocking(line)) ? 'color:#e5484d' : ''"
                      >
                        <template v-if="line.markedForRemoval">{{ $t("reservasHc.hotelResModal.willBeRemovedOnSave") }}</template>
                        <template v-else-if="line.checking">{{ $t("reservasHc.hotelResModal.checkingAvailability") }}</template>
                        <template v-else-if="isLineBlocking(line)">{{ $t("reservasHc.hotelResModal.notEnoughAvail") }} ({{ $tc("reservasHc.hotelResModal.freeCount", line.available, { count: line.available }) }})</template>
                        <template v-else>{{ $tc("reservasHc.hotelResModal.availableCount", line.available, { count: line.available }) }} · {{ $t("reservasHc.hotelResModal.total") }} {{ currencySymbol }} {{ formatAmount(line.lineTotal) }}</template>
                      </p>
                    </transition>
                  </div>
                  <p style="margin:4px 2px 0;font-size:11px;color:#9a9aae">
                    {{ tt("calendarHotel.modal.groupSharedHint", "Es una sola reserva: nombre, teléfono, email, observación y chat se comparten entre todas las habitaciones.") }}
                  </p>
                </div>
              </template>

              <!-- Habitaciones de OTRO tipo que se añaden ahora.
                   Al crear son las lineas extra de la reserva nueva; al EDITAR son
                   habitaciones que se suman a una reserva que ya existe (POST
                   /booking-group/add-line al guardar). Es lo que permite convertir
                   «2 dobles» en «1 doble + 1 deluxe» sin cancelar ni rehacer nada.
                   En una reserva de OTA no se ofrece: las habitaciones las manda el
                   canal y el servidor lo rechaza con `ota_managed`. -->
              <template v-if="form.roomType && !otaLocked">
                <transition-group name="rm-row" tag="div" class="rm-rooms__extras">
                  <div v-for="(line, idx) in extraLines" :key="line.uid" class="rm-xrow">
                    <div class="rm-room rm-room--extra">
                      <div class="rm-room__type">
                        <div class="rm-select-wrap">
                          <select
                            v-model="line.roomTypeId"
                            class="rm-input rm-select"
                            @change="onExtraTypeChange(line)"
                          >
                            <option :value="null" disabled>
                              {{ tt("calendarHotel.modal.roomTypePlaceholder", "Selecciona tipo") }}
                            </option>
                            <option
                              v-for="t in availableTypesForLine(idx)"
                              :key="t.id"
                              :value="t.id"
                            >
                              {{ t.name }}
                            </option>
                          </select>
                          <i class="fal fa-chevron-down rm-select-caret"></i>
                        </div>
                      </div>
                      <div class="rm-room__qty">
                        <input
                          type="number"
                          class="rm-input rm-input--c"
                          min="1"
                          :max="line.available != null ? line.available : undefined"
                          :disabled="!line.roomTypeId || line.available === 0"
                          v-model.number="line.quantity"
                          @input="clampExtraQty(line)"
                        />
                      </div>
                      <div v-if="!ocultaImportes" class="rm-room__price">
                        <span class="rm-cur">{{ currencySymbol }}</span>
                        <input
                          type="number"
                          class="rm-input rm-input--c"
                          v-model.number="line.pricePerNight"
                          min="0"
                          step="0.01"
                          :disabled="!line.roomTypeId"
                          @input="onExtraPerNightManual(line)"
                        />
                        <button
                          v-if="line.roomTypeId && Number(line.computedPrice) > 0 && line.priceMode === 'manual'"
                          type="button"
                          class="rm-room__auto"
                          :title="tt('calendarHotel.modal.useAutoPrice', 'Usar precio automático')"
                          @click="applyExtraAutoPrice(line)"
                        ><i class="fal fa-sync"></i></button>
                      </div>
                      <button
                        type="button"
                        class="rm-room__x rm-room__x--btn"
                        :title="tt('calendarHotel.modal.cancel', 'Quitar')"
                        @click="removeExtraLine(idx)"
                      >
                        <i class="fal fa-times"></i>
                      </button>
                    </div>
                    <transition name="rm-note">
                      <p
                        v-if="line.checking || (line.roomTypeId && line.available != null)"
                        class="rm-room__note"
                      >
                        <template v-if="line.checking">{{ $t("reservasHc.hotelResModal.checkingAvailability") }}</template>
                        <template v-else>{{ $tc("reservasHc.hotelResModal.availableCount", line.available, { count: line.available }) }}</template>
                      </p>
                    </transition>
                  </div>
                </transition-group>

                <button type="button" class="extra-type-add" @click="addExtraLine">
                  <i class="fal fa-plus"></i>
                  {{ tt("calendarHotel.modal.addType", "Añadir otro tipo de habitación") }}
                </button>
              </template>


              <!-- Total sumado -->
              <div v-if="form.roomType && nightsSelected > 0" class="rm-rooms__total">
                <span class="rm-rooms__total-lb">
                  {{ tt("calendarHotel.modal.total", "Total") }}
                  <small>· {{ $tc("reservasHc.hotelResModal.nightsCount", nightsSelected, { count: nightsSelected }) }}</small>
                </span>
                <span class="rm-rooms__total-val" :class="{ 'is-bump': totalBump }">
                  {{ currencySymbol }} {{ formatAmount(reservationTotal) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Reenviar mensaje de confirmación (solo al modificar) -->
          <div v-if="isEditMode" class="rm-resend">
            <label class="rm-resend__row">
              <input
                type="checkbox"
                class="rm-resend__cb"
                v-model="resendConfirmation"
              />
              <span class="rm-resend__tx">
                <span class="rm-resend__t">{{ $t("reservasHc.hotelResModal.resendConfirmationTitle") }}</span>
                <span class="rm-resend__s">
                  {{ $t("reservasHc.hotelResModal.resendConfirmationSub") }}
                </span>
              </span>
            </label>
            <transition name="rm-collapse">
              <div v-if="resendConfirmation" class="rm-resend__note">
                <i class="fas fa-circle-info"></i>
                <span v-html="$t('reservasHc.hotelResModal.resendConfirmationNote')"></span>
              </div>
            </transition>
          </div>
        </div>

        <!-- Footer -->
        <footer class="rm-foot">
          <button
            type="button"
            class="rm-btn rm-btn--ghost"
            @click="close"
            :disabled="loaderLocal"
          >
            {{ tt("calendarHotel.modal.cancel", "Cancelar") }}
          </button>
          <button
            type="button"
            class="rm-btn rm-btn--primary"
            :disabled="loaderLocal || disableSubmit"
            @click.prevent="submit"
          >
            <span v-if="loaderLocal" class="rm-btn__spin"></span>
            <i v-else class="fal fa-check"></i>
            {{
              isEditMode
                ? tt("calendarHotel.modal.save", "Guardar cambios")
                : tt("calendarHotel.modal.create", "Crear")
            }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { VueTelInput } from "vue-tel-input";
import Multiselect from "vue-multiselect";
import LoaderModal from "../Loaders/loaderModal.vue";
import toastr from "toastr";
import axios from "axios";
import { buscarClientes, listarClientes } from "@/services/clientesDirectorio";
import telefonoDelHuesped from "@/mixins/telefonoDelHuesped";

export default {
  name: "HotelReservationModal",
  mixins: [telefonoDelHuesped],

  props: {
    visible: { type: Boolean, default: false },
    roomTypes: { type: Array, default: () => [] },
    onCreateReservation: { type: Function, default: null },

    presetStartDate: { type: String, default: null },
    presetEndDate: { type: String, default: null },
    presetRoomType: { type: [Object, Number, String], default: null },
    // Habitación física elegida con drag en su fila del calendario:
    // { id, codigo, nombre, unitId }
    presetRoomInstance: { type: Object, default: null },

    editReservation: { type: Object, default: null },
    redirectUrl: { type: String, default: null },
    loader: { type: Boolean, default: false },
  },

  components: {
    VueTelInput,
    Multiselect,
    LoaderModal,
  },

  data() {
    return {
      localVisible: false,
      loaderLocal: false,
      resendConfirmation: false,

      form: this.getEmptyForm(),

      // Buscar un cliente guardado (lo mismo que al crear una reserva de apartamento).
      showGuestSuggestions: false,
      guestPickerQuery: "",
      guestSuggestions: [],
      guestSearchLoading: false,
      selectedGuestSuggestionId: null,

      // Líneas ADICIONALES de tipos de habitación (reserva multi-tipo, estilo OTA):
      // cada línea = { roomTypeId, quantity, price }. Comparten huésped/fechas/código.
      extraLines: [],
      extraSeq: 0, // id estable por fila (key del <transition-group>)
      // EDICIÓN de reserva COMBINADA: las OTRAS habitaciones del grupo (líneas hermanas
      // con el mismo codigo_reserva), EDITABLES en cantidad. Cada una lleva su reserva_id
      // para poder actualizarla (POST /booking-group/set-line-quantity) al guardar.
      editGroupLines: [],
      // La habitación PRINCIPAL (la que se abrió para editar) también se puede QUITAR
      // desde aquí; no se elimina hasta Guardar (con confirmación). Control total del grupo.
      primaryMarkedForRemoval: false,
      totalBump: false, // feedback breve al recalcular el Total

      availableInstances: null,
      availabilityOk: true,
      lastAvailabilityOk: null,

      ratePlans: [],

      // ✅ Precio automático vs manual
      priceMode: "auto", // 'auto' | 'manual'
      computedPricePerRoom: 0, // total calculado para 1 habitación en el rango
      _isApplyingAutoPrice: false,

      originalStartDate: null,
      originalEndDate: null,
      originalPropertyId: null,

      originalRoomTypeId: null,
      originalQuantity: null,

      _isInitializing: false,
      _refreshTimer: null,
      _lastRequestKey: null,

      _availabilityAbort: null,
      _priceAbort: null,
    };
  },

  computed: {
    /* ¿Esconderle los importes a quien mira? (perfil coanfitrión) */
    ocultaImportes() {
      return this.$store.getters.ocultaImportes;
    },

    isEditMode() {
      return !!this.editReservation;
    },

    // Habitación preseleccionada VIGENTE: solo mientras el tipo elegido siga
    // siendo el de la habitación arrastrada y se pida 1 sola habitación.
    activePresetInstance() {
      if (this.isEditMode || !this.presetRoomInstance) return null;
      const rtId = this.form.roomType?.id;
      if (!rtId || rtId !== this.presetRoomInstance.unitId) return null;
      if ((Number(this.form.quantity) || 1) !== 1) return null;
      return this.presetRoomInstance;
    },

    isOtaBooking() {
      const r = this.editReservation || {};
      return !!(r.ota_name || r.id_channel);
    },

    otaLocked() {
      return this.isEditMode && this.isOtaBooking;
    },

    // Líneas del grupo (reserva COMBINADA): todas las habitaciones que comparten
    // codigo_reserva. El backend las expone en editReservation.group_lines.
    groupLines() {
      const gl = this.editReservation && this.editReservation.group_lines;
      return Array.isArray(gl) ? gl : [];
    },

    // Las OTRAS habitaciones del grupo (distintas de la que se está editando), para
    // mostrarlas en el drawer de edición y que se vea la reserva COMPLETA.
    groupExtraRooms() {
      if (!this.isEditMode) return [];
      const currentId = Number(this.bookingId);
      return this.groupLines.filter(
        (l) => Number(l && l.reserva_id) !== currentId,
      );
    },

    // El TIPO de habitación se bloquea siempre en edición (cambiar de tipo es otro flujo).
    typeQtyLocked() {
      return this.isEditMode;
    },

    // La CANTIDAD de habitaciones sí se puede cambiar en edición de reservas directas
    // (reducir/aumentar). Solo se bloquea en reservas de OTA (las gestiona el canal).
    qtyLocked() {
      return this.otaLocked;
    },

    bookingId() {
      return this.isEditMode ? this.editReservation?.id || null : null;
    },

    user() {
      return this.$store?.state?.userRoot || null;
    },

    /**
     * De qué anuncio es la reserva. Lo dice la HABITACIÓN elegida.
     *
     * Antes era `propiedadActiva`, la del panel. Quien creaba una reserva de
     * apartamento y luego una de hotel se llevaba la segunda al anuncio de la
     * primera: la reserva quedaba con la habitación de un hotel y el anuncio de
     * otro alojamiento, y con él se resolvía la cerradura — el huésped recibía
     * el código de otro edificio.
     *
     * `propiedadActiva` queda solo como último recurso, para cuando todavía no
     * hay ninguna habitación elegida.
     */
    property_id() {
      const elegida = this.form?.roomType?.property_id;
      if (elegida) return elegida;

      const primera = (this.roomTypes || []).find((t) => t && t.property_id);
      return primera?.property_id || this.$store?.state?.propiedadActiva || null;
    },

    minDate() {
      return new Date().toISOString().split("T")[0];
    },

    minEndDate() {
      if (!this.form.startDate) return this.minDate;
      const d = new Date(this.form.startDate);
      d.setDate(d.getDate() + 1);
      return d.toISOString().split("T")[0];
    },

    nightsSelected() {
      if (!this.form.startDate || !this.form.endDate) return 0;
      const s = new Date(`${this.form.startDate}T00:00:00`);
      const e = new Date(`${this.form.endDate}T00:00:00`);
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
      const ms = e - s;
      return ms > 0 ? Math.round(ms / (1000 * 60 * 60 * 24)) : 0;
    },

    staySummaryText() {
      if (!this.form.startDate || !this.form.endDate || this.nightsSelected <= 0)
        return null;
      return this.$tc("reservasHc.hotelResModal.nightsCount", this.nightsSelected, {
        count: this.nightsSelected,
      });
    },

    http() {
      return this.$axios || axios;
    },

    selectedRoomTypeInfo() {
      return this.form.roomType || {};
    },

    maxQuantityAllowed() {
      const base = Number(
        this.availableInstances ??
          this.selectedRoomTypeInfo?.available_instances ??
          1,
      );
      return Number.isFinite(base) && base > 0 ? base : 1;
    },

    availableInstancesLabel() {
      const v =
        this.availableInstances ??
        this.selectedRoomTypeInfo?.available_instances;
      return v == null ? "-" : v;
    },

    disableSubmit() {
      if (this.otaLocked) return false;
      if (!this.form.roomType || !this.form.startDate || !this.form.endDate)
        return true;
      if (!this.availabilityOk) return true;
      // Alguna habitación (extra al crear, o del grupo al editar) sin disponibilidad
      // suficiente → no dejar guardar.
      if (this.blockingLines.length) return true;
      return false;
    },

    isManualPrice() {
      return this.priceMode === "manual";
    },

    hasComputedPrice() {
      return Number(this.computedPricePerRoom) > 0;
    },

    computedTotalAutoPrice() {
      const q = Math.max(1, Number(this.form.quantity) || 1);
      const base = Number(this.computedPricePerRoom) || 0;
      return base * q;
    },

    // Líneas extra que YA tienen un tipo elegido (para el desglose de precios).
    /** El hotel al que pertenece la reserva. */
    hotelDeLaPrincipal() {
      /* Al EDITAR manda la propiedad de la reserva, no el tipo que haya quedado
         en el formulario: si el tipo no se pudo resolver, `applyEditReservation`
         cae en `roomTypes[0]`, que con varios hoteles puede ser de OTRO. Y si el
         hotel se calcula mal, el filtro ofrece las habitaciones equivocadas. */
      if (this.isEditMode) {
        const r = this.editReservation || {};
        const prop = r.propiedad_id ?? r.property_id ?? null;
        if (prop != null) {
          const ref = (this.roomTypes || []).find(
            (x) => Number(x.property_id) === Number(prop),
          );
          if (ref) return this.hotelDe(ref);
        }
      }

      return this.hotelDe(this.form.roomType);
    },
    extraLinesWithType() {
      return this.extraLines.filter((l) => l.roomTypeId);
    },
    hasExtraLines() {
      return this.extraLinesWithType.length > 0;
    },
    // Total de la reserva = precio principal + tipos extra VÁLIDOS (excluye líneas con 0
    // disponibilidad; antes las sumaba mal) + habitaciones del grupo en edición.
    reservationTotal() {
      const main = this.primaryMarkedForRemoval ? 0 : Number(this.form.price) || 0;
      const extras = this.extraLinesWithType.reduce(
        (acc, l) => acc + (this.isLineBlocking(l) ? 0 : Number(l.price) || 0),
        0,
      );
      const groupExtras = (this.editGroupLines || []).reduce(
        (acc, l) =>
          acc +
          (l.markedForRemoval || this.isLineBlocking(l)
            ? 0
            : Number(l.lineTotal) || 0),
        0,
      );
      return Math.round((main + extras + groupExtras) * 100) / 100;
    },

    // Líneas (extra al crear + del grupo al editar) que IMPIDEN guardar: 0 disponibilidad
    // o cantidad pedida > libre. No suman al total (ver reservationTotal) ni dejan guardar.
    blockingLines() {
      const create = this.extraLinesWithType.filter((l) => this.isLineBlocking(l));
      const edit = (this.editGroupLines || []).filter((l) => this.isLineBlocking(l));
      return create.concat(edit);
    },

    currencyCode() {
      return (
        this.form.roomType?.currency ||
        this.ratePlans?.[0]?.currency ||
        "EUR"
      );
    },

    currencySymbol() {
      if (this.ocultaImportes) return "";
      const map = {
        EUR: "€", USD: "$", COP: "$", MXN: "$", ARS: "$", CLP: "$",
        GBP: "£", BRL: "R$", PEN: "S/", JPY: "¥", CHF: "CHF",
      };
      return map[this.currencyCode] || this.currencyCode;
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.localVisible = !!v;
        if (v) this.initFormOnOpen();
      },
    },

    localVisible(v) {
      if (v !== this.visible) this.$emit("update:visible", v);
    },

    roomTypes: {
      deep: true,
      handler() {
        if (!this.localVisible) return;
        if (!this.isEditMode) return;

        if (!this.form.roomType && this.originalRoomTypeId) {
          const rt = this.resolveRoomType(this.originalRoomTypeId);
          if (rt) this.form.roomType = rt;
        }

        if (
          !this.otaLocked &&
          this.form.roomType &&
          this.form.startDate &&
          this.form.endDate
        ) {
          this._lastRequestKey = null;
          this.scheduleRefresh(0);
        }
      },
    },

    editReservation: {
      deep: true,
      handler() {
        if (this.localVisible) this.initFormOnOpen();
      },
    },

    presetStartDate() {
      if (this.localVisible && !this.isEditMode) this.applyPresetsOnly();
    },
    presetEndDate() {
      if (this.localVisible && !this.isEditMode) this.applyPresetsOnly();
    },
    presetRoomType() {
      if (this.localVisible && !this.isEditMode) this.applyPresetsOnly();
    },

    "form.price"(newVal) {
      this.form.subtotal = Number(newVal) || 0;
      this.calculateTotal();
    },

    // Pulso breve del Total cuando cambia (CSS lo desactiva en reduced-motion).
    reservationTotal(nv, ov) {
      if (nv === ov) return;
      this.totalBump = true;
      clearTimeout(this.bumpTimer);
      this.bumpTimer = setTimeout(() => {
        this.totalBump = false;
      }, 340);
    },

    "form.promotions": {
      handler() {
        this.calculateTotal();
      },
      deep: true,
    },
    "form.taxes": {
      handler() {
        this.calculateTotal();
      },
      deep: true,
    },

    "form.startDate"(val) {
      if (!val || this._isInitializing) return;

      if (this.form.endDate && new Date(this.form.endDate) <= new Date(val)) {
        const nd = new Date(val);
        nd.setDate(nd.getDate() + 1);
        this.form.endDate = nd.toISOString().split("T")[0];
      }

      if (!this.otaLocked && this.form.roomType) this.scheduleRefresh();
    },

    "form.endDate"(val) {
      if (!val || this._isInitializing) return;
      if (!this.otaLocked && this.form.roomType) this.scheduleRefresh();
    },

    "form.quantity"() {
      if (this._isInitializing) return;

      this.validateGuestsNumber();

      // ✅ Solo recalcular si estamos en modo auto
      this.recalcPriceByQuantity();

      if (!this.otaLocked && this.form.roomType) this.scheduleRefresh();
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.handleOutsideGuestPicker);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideGuestPicker);
    clearTimeout(this._guestSearchTimer);
  },

  methods: {
    tt(key, fallback) {
      const v = typeof this.$t === "function" ? this.$t(key) : key;
      return v && v !== key ? v : fallback;
    },

    formatMoney(value) {
      if (this.ocultaImportes) return "";
      const n = Number(value) || 0;
      try {
        return new Intl.NumberFormat("es-ES", { useGrouping: "always",
          style: "currency",
          currency: "EUR",
        }).format(n);
      } catch (e) {
        return `${n.toFixed(2)} €`;
      }
    },

    getEmptyForm() {
      const today = new Date().toISOString().split("T")[0];
      return {
        roomType: null,
        quantity: 1,
        name: "",
        phone: "",
        email: "",
        observacion: "",
        startDate: today,
        endDate: null,
        number_of_guests: 1,

        selectShippingDate: "automatico",
        typeSend: false,

        price: 0,
        pricePerNight: 0,
        rate: null,

        promotions: [],
        taxes: [],
        subtotal: 0,
        total: 0,
      };
    },

    normalizePhone(v) {
      // «0049…» es internacional: el 00 pasa a «+» ANTES de anteponerlo, o quedaba «+0049…».
      const s = String(v || "").replace(/\s+/g, "").replace(/^00(?=\d)/, "+");
      if (!s) return "";
      return s.startsWith("+") ? s : `+${s.replace(/^\+*/, "")}`;
    },

    // ¿La línea (extra al crear o del grupo al editar) impide guardar? 0 disponibilidad o
    // cantidad pedida mayor que lo libre. Una línea marcada para QUITAR nunca bloquea.
    // Si aún no se comprobó (available null) no bloquea.
    isLineBlocking(l) {
      if (!l || l.markedForRemoval) return false;
      const av = l.available;
      if (av == null) return false;
      const q = Number(l.quantity) || 0;
      return Number(av) === 0 || q > Number(av);
    },

    // Recalcula el total de una línea del grupo = precio/noche × cantidad × noches.
    recalcEditGroupLineTotal(line) {
      const nights = Math.max(1, Number(this.nightsSelected) || 1);
      const pn = Math.max(0, Number(line.pricePerNight) || 0);
      const q = Math.max(1, Number(line.quantity) || 1);
      line.lineTotal = Math.round(pn * q * nights * 100) / 100;
    },

    // Precio MANUAL de una habitación del grupo: marca que se editó a mano y recalcula su
    // total (el backend persiste este precio en vez de re-cotizar).
    onEditGroupPriceChange(line) {
      line.priceEdited = true;
      this.recalcEditGroupLineTotal(line);
    },

    // Marcar/desmarcar una habitación del grupo para QUITARLA. No se elimina hasta GUARDAR
    // (con confirmación). Mientras esté marcada, no suma al total ni bloquea.
    toggleEditGroupRemoval(line) {
      line.markedForRemoval = !line.markedForRemoval;
    },

    // EDICIÓN: comprueba la disponibilidad de una habitación del grupo. El backend excluye
    // las habitaciones del PROPIO grupo (via booking_id) → available = lo que esta reserva
    // puede llegar a ocupar en ese tipo. Limita la cantidad a ese máximo.
    async checkEditGroupAvailability(line) {
      if (!line || !line.unitId || !this.form.startDate || !this.form.endDate) return;
      line.checking = true;
      try {
        const { data } = await this.http.post(
          "/validate/hotel-availability",
          {
            accommodation_type: { id: line.unitId },
            quantity: Number(line.quantity) || 1,
            start_date: this.form.startDate,
            end_date: this.form.endDate,
            booking_id: line.reservaId || null,
          },
          { validateStatus: (s) => s < 500 },
        );
        const n = Number(data && (data.available_units ?? data.available));
        line.available = Number.isFinite(n) ? Math.max(0, n) : null;
      } catch (e) {
        line.available = null;
      } finally {
        line.checking = false;
      }
    },

    // EDICIÓN: al cambiar la cantidad de una habitación del grupo, la acota al máximo libre
    // (si ya se comprobó) y reestima su total (el backend recotiza al guardar).
    onEditGroupQtyChange(line) {
      let q = Math.max(1, Number(line.quantity) || 1);
      if (line.available != null && q > Number(line.available)) {
        q = Math.max(1, Number(line.available));
      }
      line.quantity = q;
      this.recalcEditGroupLineTotal(line);
      // Re-comprobar disponibilidad con la nueva cantidad (por si sube).
      this.checkEditGroupAvailability(line);
    },

    // ── Reserva multi-tipo: líneas adicionales de tipo de habitación ──
    addExtraLine() {
      this.extraLines.push({
        uid: this.extraSeq++,
        roomTypeId: null, quantity: 1, available: null, checking: false,
        // Precio POR TIPO: auto (noches × tarifa × cantidad) o manual (ajuste del anfitrión).
        price: 0, pricePerNight: 0, computedPrice: 0, priceMode: "auto", pricing: false,
      });
    },
    removeExtraLine(idx) {
      this.extraLines.splice(idx, 1);
    },
    resolveRoomTypeById(id) {
      const list = Array.isArray(this.roomTypes) ? this.roomTypes : [];
      return list.find((t) => Number(t.id) === Number(id)) || null;
    },

    // Tipos seleccionables en una línea extra: omite el tipo PRINCIPAL y los que ya
    // estén elegidos en OTRAS líneas extra (no se repiten tipos).
    availableTypesForLine(idx) {
      const list = Array.isArray(this.roomTypes) ? this.roomTypes : [];
      const mainId = this.form.roomType?.id || null;
      const usedElsewhere = this.extraLines
        .filter((_, i) => i !== idx)
        .map((l) => l.roomTypeId)
        .filter((v) => v != null);

      // Al editar, los tipos que la reserva YA tiene tampoco se ofrecen: para poner
      // mas habitaciones de un tipo que ya esta, se sube su cantidad en su propia fila.
      const yaEnLaReserva = (this.editGroupLines || [])
        .filter((l) => !l.markedForRemoval)
        .map((l) => Number(l.unitId ?? l.accommodation_unit_id))
        .filter((v) => Number.isFinite(v));

      /* Y SOLO del mismo hotel que la habitación principal.
         Una reserva no puede tener habitaciones de dos hoteles: cada línea se
         crea con una llamada aparte y el código de grupo que las une no puede
         cruzar alojamientos, así que la primera se creaba y la segunda moría
         con «Ese código de grupo ya pertenece a otra reserva», dejando media
         reserva hecha y al operador sin saber qué había pasado. */
      const hotel = this.hotelDeLaPrincipal;

      return list.filter(
        (t) => t.id !== mainId
          && !usedElsewhere.includes(t.id)
          && !yaEnLaReserva.includes(Number(t.id))
          && (hotel == null || this.hotelDe(t) === hotel),
      );
    },

    /** De qué hotel es un tipo de habitación. Por anuncio; la propiedad es el respaldo. */
    hotelDe(t) {
      if (!t) return null;
      return t.listing_id ?? (t.property_id != null ? `p${t.property_id}` : null);
    },

    // Al elegir un tipo en una línea extra, consulta su disponibilidad para el rango
    // y limita la cantidad a lo realmente libre.
    async onExtraTypeChange(line) {
      line.available = null;
      line.quantity = 1;
      // Reset del precio de la línea al cambiar de tipo (se recalcula abajo).
      line.priceMode = "auto";
      line.price = 0;
      line.pricePerNight = 0;
      line.computedPrice = 0;
      const rt = this.resolveRoomTypeById(line.roomTypeId);
      if (!rt || !this.form.startDate || !this.form.endDate) return;
      line.checking = true;
      try {
        const { data } = await this.http.post(
          "/validate/hotel-availability",
          {
            accommodation_type: rt,
            quantity: 1,
            start_date: this.form.startDate,
            end_date: this.form.endDate,
          },
          { validateStatus: (s) => s < 500 },
        );
        const n = Number(data?.available_units ?? data?.available);
        line.available = Number.isFinite(n) ? Math.max(0, n) : null;
      } catch (e) {
        line.available = null;
      } finally {
        line.checking = false;
      }
      // Precio automático de ESTE tipo para el rango (mismo endpoint que el principal).
      this.fetchExtraLinePrice(line);
    },

    clampExtraQty(line) {
      let q = Number(line.quantity) || 1;
      if (q < 1) q = 1;
      if (line.available != null && q > line.available) q = line.available;
      line.quantity = q;
      this.recalcExtraLinePrice(line);
    },

    // ── Precio POR NOCHE, por tipo de habitación ───────────────────────────
    roundMoney(v) {
      return Math.round((Number(v) || 0) * 100) / 100;
    },
    formatAmount(v) {
      if (this.ocultaImportes) return "";
      return (Number(v) || 0).toLocaleString("es-ES", { useGrouping: "always",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    extraLineName(line) {
      const rt = this.resolveRoomTypeById(line.roomTypeId);
      return rt?.name || "—";
    },

    // ── Tipo PRINCIPAL ──
    // Precio/noche sugerido = total del rango para 1 hab ÷ noches (solo en auto).
    syncPrimaryPerNightFromComputed() {
      if (this.priceMode !== "auto") return;
      const n = this.nightsSelected;
      this.form.pricePerNight =
        n > 0 ? this.roundMoney(this.computedPricePerRoom / n) : 0;
    },
    // Total de la línea principal = precio/noche × cantidad × noches.
    applyPrimaryTotal() {
      const n = this.nightsSelected;
      const q = Math.max(1, Number(this.form.quantity) || 1);
      this.form.price = this.roundMoney(
        (Number(this.form.pricePerNight) || 0) * q * n,
      );
    },
    onPrimaryPerNightManual() {
      // v-model ya actualizó form.pricePerNight; marcamos manual y recalculamos el total.
      this.priceMode = "manual";
      this.applyPrimaryTotal();
    },

    // ── Tipos EXTRA ──
    // Total del rango para 1 habitación de ese tipo (mismo endpoint que el principal).
    async fetchExtraLinePrice(line) {
      const rt = this.resolveRoomTypeById(line.roomTypeId);
      if (!rt || !this.form.startDate || !this.form.endDate) {
        line.computedPrice = 0;
        return;
      }
      line.pricing = true;
      try {
        const { data } = await this.http.post(
          "/validate/night-price-availability",
          {
            accommodation_type: rt,
            start_date: this.form.startDate,
            end_date: this.form.endDate,
            rate: null,
          },
          { validateStatus: (s) => s < 500 },
        );
        line.computedPrice = Number(data?.totalPrice) || 0;
        if (line.priceMode === "auto") {
          const n = this.nightsSelected;
          line.pricePerNight =
            n > 0 ? this.roundMoney(line.computedPrice / n) : 0;
        }
      } catch (e) {
        line.computedPrice = 0;
      } finally {
        line.pricing = false;
        this.applyExtraTotal(line);
      }
    },
    refreshExtraLinesPricing() {
      for (const line of this.extraLines) {
        if (line.roomTypeId) this.fetchExtraLinePrice(line);
      }
    },
    // Total de una línea extra = precio/noche × cantidad × noches.
    applyExtraTotal(line) {
      const n = this.nightsSelected;
      const q = Math.max(1, Number(line.quantity) || 1);
      line.price = this.roundMoney(
        (Number(line.pricePerNight) || 0) * q * n,
      );
    },
    // Al cambiar la cantidad de una línea extra, reescala su total (precio/noche fijo).
    recalcExtraLinePrice(line) {
      this.applyExtraTotal(line);
    },
    onExtraPerNightManual(line) {
      // v-model ya actualizó line.pricePerNight; marcamos manual y recalculamos el total.
      line.priceMode = "manual";
      this.applyExtraTotal(line);
    },
    applyExtraAutoPrice(line) {
      if (!(Number(line.computedPrice) > 0)) return;
      line.priceMode = "auto";
      const n = this.nightsSelected;
      line.pricePerNight = n > 0 ? this.roundMoney(line.computedPrice / n) : 0;
      this.applyExtraTotal(line);
    },

    onPriceUserInput() {
      // ✅ Si el cambio viene del usuario, pasamos a manual y NO dejamos que auto lo pise
      if (this._isApplyingAutoPrice) return;
      this.priceMode = "manual";
    },

    applyAutoPrice() {
      // ✅ Vuelve a usar el precio/noche sugerido automático
      if (!this.hasComputedPrice) return;
      this.priceMode = "auto";
      this.syncPrimaryPerNightFromComputed();
      this.applyPrimaryTotal();
    },

    setPriceSafely(value) {
      this._isApplyingAutoPrice = true;
      this.form.price = Number(value) || 0;
      this._isApplyingAutoPrice = false;
    },

    toggleGuestPicker() {
      if (this.showGuestSuggestions) {
        this.showGuestSuggestions = false;
        return;
      }
      this.guestPickerQuery = "";
      this.showGuestSuggestions = true;
      this.searchGuests();
      this.$nextTick(() => {
        if (this.$refs.guestPickerInput) this.$refs.guestPickerInput.focus();
      });
    },

    searchGuests() {
      clearTimeout(this._guestSearchTimer);
      const text = (this.guestPickerQuery || "").trim();

      this._guestSearchTimer = setTimeout(async () => {
        this.guestSearchLoading = true;
        try {
          // Sin texto, los últimos clientes; con texto, los que coincidan.
          const items = text.length >= 2
            ? await buscarClientes(text)
            : (await listarClientes({ perPage: 20 })).data || [];
          if ((this.guestPickerQuery || "").trim() !== text) return;
          this.guestSuggestions = items;
        } catch (e) {
          console.error("Guest search error:", e);
          this.guestSuggestions = [];
        } finally {
          this.guestSearchLoading = false;
        }
      }, text ? 250 : 0);
    },

    applyGuestSuggestion(guest) {
      this.selectedGuestSuggestionId = guest.id;
      this.form.name = [guest.nombre, guest.apellido].filter(Boolean).join(" ").trim();
      this.form.email = guest.email || "";
      this.form.phone = guest.telefono || "";
      this.guestSuggestions = [];
      this.showGuestSuggestions = false;
    },

    handleOutsideGuestPicker(event) {
      const wrap = this.$refs.guestAutocompleteWrap;
      if (this.showGuestSuggestions && wrap && !wrap.contains(event.target)) {
        this.showGuestSuggestions = false;
      }
    },

    close() {
      this.localVisible = false;
      this.showGuestSuggestions = false;
      this.selectedGuestSuggestionId = null;

      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;

      this._lastRequestKey = null;
      this.lastAvailabilityOk = null;

      if (this._availabilityAbort) this._availabilityAbort.abort();
      if (this._priceAbort) this._priceAbort.abort();
      this._availabilityAbort = null;
      this._priceAbort = null;
    },

    initFormOnOpen() {
      this._isInitializing = true;

      this.form = this.getEmptyForm();
      this.extraLines = [];
      this.editGroupLines = [];
      this.primaryMarkedForRemoval = false;
      this.resendConfirmation = false;

      this.availableInstances = null;
      this.availabilityOk = true;
      this.lastAvailabilityOk = null;

      this.ratePlans = [];

      // ✅ Reset pricing state
      this.computedPricePerRoom = 0;
      this._isApplyingAutoPrice = false;

      this.originalStartDate = null;
      this.originalEndDate = null;
      this.originalPropertyId = null;

      this.originalRoomTypeId = null;
      this.originalQuantity = null;

      this._lastRequestKey = null;

      if (this.isEditMode) {
        // ✅ En editar: respeta el precio guardado (manual por defecto)
        this.priceMode = "manual";
        this.applyEditReservation();
      } else {
        // ✅ En crear: auto por defecto
        this.priceMode = "auto";
        this.applyPresetsOnly();
      }

      if (
        !this.form.endDate ||
        new Date(this.form.endDate) <= new Date(this.form.startDate)
      ) {
        const nd = new Date(this.form.startDate);
        nd.setDate(nd.getDate() + 1);
        this.form.endDate = nd.toISOString().split("T")[0];
      }

      this.$nextTick(() => {
        this._isInitializing = false;

        // OTA: no refrescamos disponibilidad/precio en vivo
        if (
          !this.otaLocked &&
          this.form.roomType &&
          this.form.startDate &&
          this.form.endDate
        ) {
          this.scheduleRefresh(0);
        }
      });
    },

    applyPresetsOnly() {
      if (this.presetStartDate) this.form.startDate = this.presetStartDate;
      if (this.presetEndDate) this.form.endDate = this.presetEndDate;

      if (this.presetRoomType) {
        const rt = this.resolveRoomType(this.presetRoomType);
        if (rt) this.form.roomType = rt;
      }
    },

    applyEditReservation() {
      const r = this.editReservation || {};

      const s = (r.start_date || r.fecha_ingreso || "").split("T")[0];
      const e = (r.end_date || r.fecha_salida || "").split("T")[0];

      this.form.startDate = s || this.form.startDate;
      this.form.endDate = e || this.form.endDate;

      this.originalStartDate = this.form.startDate;
      this.originalEndDate = this.form.endDate;

      const op =
        Number(
          r.original_property_id ||
            r.propiedad_id ||
            r.property_id ||
            this.property_id ||
            0,
        ) || null;
      this.originalPropertyId = op;

      this.form.name = r.nombre || r.name || "";
      this.form.phone = r.telefono || r.phone || "";
      this.form.email = (r.email || "").trim();
      this.form.observacion = r.observacion || r.observation || "";

      const rooms = Number(r.number_of_rooms || r.quantity || r.rooms || 1);
      this.form.quantity = Number.isFinite(rooms) && rooms > 0 ? rooms : 1;

      const guests = Number(
        r.cantidad_huesped || r.number_of_guests || r.guests || 1,
      );
      this.form.number_of_guests =
        Number.isFinite(guests) && guests > 0 ? guests : 1;

      const price = Number(r.price || 0);
      this.form.price = Number.isFinite(price) ? price : 0;
      this.form.subtotal = this.form.price;
      // Deriva el precio/noche desde el total guardado (para el editor por noche).
      {
        const n = this.nightsSelected;
        const q = Math.max(1, Number(this.form.quantity) || 1);
        this.form.pricePerNight =
          n > 0 ? this.roundMoney(this.form.price / (q * n)) : 0;
      }
      this.calculateTotal();

      const unitId =
        Number(
          r.accommodation_type_id ||
            r.accommodation_unit_id ||
            r.room_type_id ||
            r.roomType?.id ||
            r.room_type?.id ||
            r.accommodation_type?.id ||
            r.accommodationUnit?.id ||
            null,
        ) || null;

      let rt = unitId ? this.resolveRoomType(unitId) : null;

      if (!rt) {
        const rtGuess =
          r.roomType ||
          r.room_type ||
          r.accommodation_type ||
          r.accommodation_type_name ||
          r.room_type_name ||
          null;

        rt = rtGuess ? this.resolveRoomType(rtGuess) : null;
      }

      if (!rt && this.roomTypes?.length) rt = this.roomTypes[0];
      if (rt) this.form.roomType = rt;

      this.originalRoomTypeId =
        Number(this.form.roomType?.id || unitId || null) || null;
      this.originalQuantity = Number(this.form.quantity) || 1;

      // ✅ Importante: en editar, el precio guardado manda, no lo pises con auto
      this.priceMode = "manual";

      // EDICIÓN de reserva COMBINADA: cargar las OTRAS habitaciones del grupo como líneas
      // EDITABLES (cantidad). Se excluye la línea que se está editando (es la principal del
      // modal). Cada una lleva su reserva_id para actualizarla al guardar.
      this.editGroupLines = [];
      const groupLines = Array.isArray(r.group_lines) ? r.group_lines : [];
      const currentId = Number(r.id);
      const nights = Math.max(1, Number(this.nightsSelected) || 1);
      for (const l of groupLines) {
        if (Number(l.reserva_id) === currentId) continue;
        const qty = Math.max(1, Number(l.rooms) || 1);
        const total = Number(l.total_to_pay) || 0;
        const perNight = qty > 0 && nights > 0 ? total / (qty * nights) : total;
        this.editGroupLines.push({
          reservaId: Number(l.reserva_id),
          unitId: Number(l.accommodation_unit_id),
          unitName: l.unit_name || this.$t("reservasHc.hotelResModal.room"),
          quantity: qty,
          originalQuantity: qty,
          guests: Number(l.guests) || 1,
          pricePerNight: Math.round(perNight * 100) / 100,
          lineTotal: total,
          originalPrice: total,
          priceEdited: false, // true si el operador editó el precio a mano
          markedForRemoval: false, // se quita al GUARDAR, no antes
          available: null,
          checking: false,
        });
      }
      this.editGroupLines.forEach((l) => this.checkEditGroupAvailability(l));
    },

    resolveRoomType(val) {
      const list = this.roomTypes || [];

      if (val && typeof val === "object" && val.id) {
        const found = list.find((x) => Number(x.id) === Number(val.id));
        return found || val;
      }

      const id = Number(val);
      if (Number.isFinite(id) && id > 0) {
        return list.find((x) => Number(x.id) === id) || null;
      }

      const name = String(val || "")
        .toLowerCase()
        .trim();
      if (name) {
        return (
          list.find(
            (x) =>
              String(x.name || x.nombre || "")
                .toLowerCase()
                .trim() === name,
          ) || null
        );
      }

      return null;
    },

    getMaxGuestsPerRoom() {
      const rt = this.selectedRoomTypeInfo || {};
      const raw = rt.max_occupancy ?? rt.maxGuests ?? rt.capacity ?? 2;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 2;
    },

    validateGuestsNumber() {
      const qty = Math.max(1, Number(this.form.quantity) || 1);
      const maxPerRoom = this.getMaxGuestsPerRoom();
      const maxAllowed = qty * maxPerRoom;

      let g = Number(this.form.number_of_guests);
      if (!Number.isFinite(g) || g < 1) g = 1;
      g = Math.floor(g);
      if (g > maxAllowed) g = maxAllowed;

      this.form.number_of_guests = g;
    },

    validateQuantityInput(event) {
      if (this.typeQtyLocked) return;

      const value = event?.value ?? this.form.quantity;
      const maxAvailable = this.maxQuantityAllowed;

      let q = Number(value);
      if (!Number.isFinite(q) || q < 1) q = 1;
      if (q > maxAvailable) q = maxAvailable;

      this.form.quantity = q;
      this.validateGuestsNumber();
      this.recalcPriceByQuantity();
    },

    // Selector con buscador de tipo de habitación (guarda el OBJETO en form.roomType).
    onRoomTypePicked(o) {
      if (this.typeQtyLocked) return;
      const hotelAntes = this.hotelDeLaPrincipal;
      this.form.roomType = o || null;

      /* Si la principal cambia de HOTEL, las líneas extra del hotel anterior ya
         no valen: una reserva vive en un solo hotel. Se vacían en vez de
         dejarlas ahí calladas para que revienten al crear. */
      const hotelAhora = this.hotelDeLaPrincipal;
      if (hotelAntes != null && hotelAhora != null && hotelAntes !== hotelAhora) {
        const sobran = (this.extraLines || []).filter(
          (l) => l.roomTypeId && this.hotelDe(this.resolveRoomTypeById(l.roomTypeId)) !== hotelAhora,
        );
        if (sobran.length) {
          this.extraLines = (this.extraLines || []).filter((l) => !sobran.includes(l));
          toastr.info(this.$t("reservasHc.hotelResModal.otherHotelLinesDropped"));
        }
      }

      this.onRoomTypeChanged();
    },

    // Stepper de cantidad (reemplaza el InputNumber de PrimeVue).
    qtyInc() {
      if (this.qtyLocked) return;
      if ((Number(this.form.quantity) || 1) < this.maxQuantityAllowed) {
        this.form.quantity = (Number(this.form.quantity) || 1) + 1;
      }
    },
    qtyDec() {
      if (this.qtyLocked) return;
      if ((Number(this.form.quantity) || 1) > 1) {
        this.form.quantity = (Number(this.form.quantity) || 1) - 1;
      }
    },
    clampMainQty() {
      if (this.qtyLocked) return;
      let q = Number(this.form.quantity) || 1;
      if (q < 1) q = 1;
      if (q > this.maxQuantityAllowed) q = this.maxQuantityAllowed;
      this.form.quantity = q;
    },

    onRoomTypeChanged() {
      if (this.typeQtyLocked) return;
      this._lastRequestKey = null;

      // ✅ Si el usuario cambia tipo, por defecto mantenemos el modo (manual se respeta)
      this.scheduleRefresh(0);
    },

    calculateTotal() {
      const subtotal = Number(this.form.subtotal ?? this.form.price) || 0;
      const totalPromotions = (this.form.promotions || []).reduce(
        (acc, p) => acc + (Number(p.price) || 0),
        0,
      );
      const totalTaxes = (this.form.taxes || []).reduce(
        (acc, t) => acc + (Number(t.price) || 0),
        0,
      );
      this.form.total = subtotal - totalPromotions + totalTaxes;
    },

    recalcPriceByQuantity() {
      // El total escala con la cantidad (precio/noche fijo, sea auto o manual).
      this.applyPrimaryTotal();
    },

    scheduleRefresh(delay = 250) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => {
        this.refreshAvailabilityAndPrice();
      }, delay);
    },

    buildRequestKey() {
      const rtId = this.form.roomType?.id || "";
      const s = this.form.startDate || "";
      const e = this.form.endDate || "";
      const q = Number(this.form.quantity) || 1;
      const b = this.bookingId || 0;
      return `${rtId}|${s}|${e}|${q}|${b}`;
    },

    async refreshAvailabilityAndPrice() {
      if (this._isInitializing) return;
      if (!this.form.roomType || !this.form.startDate || !this.form.endDate)
        return;
      if (this.otaLocked) return;

      const key = this.buildRequestKey();
      if (key === this._lastRequestKey) return;
      this._lastRequestKey = key;

      await this.checkAvailability();

      if (this.availabilityOk) {
        // ✅ Podemos seguir consultando tarifas para "sugerido",
        // pero SOLO aplicamos al input si está en modo auto.
        await this.checkNightPriceAvailability();
      } else {
        this.ratePlans = [];
        this.computedPricePerRoom = 0;
      }

      // Las líneas extra comparten las mismas fechas → mantener su precio auto al día.
      this.refreshExtraLinesPricing();
    },

    async checkAvailability() {
      if (this._availabilityAbort) this._availabilityAbort.abort();
      this._availabilityAbort = new AbortController();

      try {
        const response = await this.http.post(
          "/validate/hotel-availability",
          {
            accommodation_type: this.form.roomType,
            start_date: this.form.startDate,
            end_date: this.form.endDate,
            quantity: Number(this.form.quantity) || 1,
            booking_id: this.bookingId || null,
          },
          { signal: this._availabilityAbort.signal },
        );

        const units = response?.data?.available_units ?? null;
        this.availableInstances = units;

        const ok = (Number(units) || 0) >= (Number(this.form.quantity) || 1);
        this.availabilityOk = ok;

        if (this.lastAvailabilityOk !== false && ok === false) {
          toastr.warning(
            this.$t("reservasHc.hotelResModal.noEnoughUnitsFree", { units: units ?? 0 }),
          );
        }
        this.lastAvailabilityOk = ok;
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;

        const code = err?.response?.data?.code || null;
        const msg =
          err?.response?.data?.error ||
          (code === "closed_period_manual"
            ? this.$t("reservasHc.hotelResModal.closedPeriodManual")
            : this.$t("reservasHc.hotelResModal.noAvailForDates"));

        this.availableInstances = 0;
        this.availabilityOk = false;

        if (this.lastAvailabilityOk !== false) toastr.warning(msg);
        this.lastAvailabilityOk = false;
      }
    },

    async checkNightPriceAvailability() {
      if (this._priceAbort) this._priceAbort.abort();
      this._priceAbort = new AbortController();

      try {
        const response = await this.http.post(
          "/validate/night-price-availability",
          {
            accommodation_type: this.form.roomType,
            start_date: this.form.startDate,
            end_date: this.form.endDate,
            rate: this.form.rate,
          },
          { signal: this._priceAbort.signal },
        );

        // ✅ base = total del rango para 1 habitación
        const base = Number(response?.data?.totalPrice) || 0;

        this.computedPricePerRoom = base;
        this.ratePlans = response?.data?.ratePlans || [];

        // Precio/noche sugerido (solo en auto) + total de la línea principal.
        this.syncPrimaryPerNightFromComputed();
        this.applyPrimaryTotal();
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;

        this.ratePlans = [];
        this.computedPricePerRoom = 0;

        // Si estabas en auto y no hay tarifas, no fuerces nada
        toastr.warning(
          this.tt(
            "calendarHotel.errors.noRatesDetail",
            "No hay tarifas disponibles para esas fechas.",
          ),
        );
      }
    },

    validateBeforeSubmit() {
      if (!this.form.roomType)
        return toastr.warning(this.$t("reservasHc.hotelResModal.selectRoomType")), false;
      if (!this.form.startDate || !this.form.endDate)
        return toastr.warning(this.$t("reservasHc.hotelResModal.selectValidDates")), false;
      if (!this.form.name)
        return toastr.warning(this.$t("reservasHc.hotelResModal.enterGuestName")), false;
      const email = String(this.form.email || "").trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toastr.warning(this.$t("reservasHc.hotelResModal.invalidEmail"));
        return false;
      }

      const phoneDigits = String(this.form.phone || "").replace(/\D/g, "");
      if (phoneDigits.length < 5)
        return toastr.warning(this.$t("reservasHc.hotelResModal.enterValidPhone")), false;
      // Prefijo de otro país (ver mixins/telefonoDelHuesped): se avisa una vez al crear.
      if (!this.isEditMode && this.telefonoParaRevisar()) return false;

      if (!this.otaLocked && !this.availabilityOk) {
        toastr.warning(this.$t("reservasHc.hotelResModal.noAvailCreateModify"));
        return false;
      }

      // Ninguna habitación (extra al crear, o del grupo al editar) puede quedar con 0
      // disponibilidad o pedir más de lo libre.
      const blocked = this.blockingLines;
      if (blocked.length) {
        const names = blocked
          .map(
            (l) =>
              l.unitName ||
              this.resolveRoomTypeById(l.roomTypeId)?.name ||
              this.$t("reservasHc.hotelResModal.aRoom"),
          )
          .join(", ");
        toastr.warning(this.$t("reservasHc.hotelResModal.notEnoughAvailIn", { names }));
        return false;
      }

      return true;
    },

    buildUpdatePayload() {
      const bookingId = this.bookingId;

      const startDate = this.otaLocked
        ? this.originalStartDate || this.form.startDate
        : this.form.startDate;
      const endDate = this.otaLocked
        ? this.originalEndDate || this.form.endDate
        : this.form.endDate;

      const lockedRoomType = this.isEditMode
        ? this.resolveRoomType(this.originalRoomTypeId) || this.form.roomType
        : this.form.roomType;

      // En OTA la cantidad la gestiona el canal (se conserva la original); en reservas
      // directas se permite la cantidad editada (reducir/aumentar habitaciones).
      const lockedQty = this.otaLocked
        ? Number(this.originalQuantity) || 1
        : Number(this.form.quantity) || 1;

      return {
        // En EDICIÓN la propiedad de la reserva es INMUTABLE (el modal de hotel no
        // tiene selector de propiedad). Enviar this.property_id (= store.propiedadActiva)
        // provocaba un "propertyChanged" espurio en el backend cuando la propiedad activa
        // del panel no coincidía con la de la reserva → disparaba reasignación de
        // habitación + recompute de disponibilidad + push a Channex en una edición que no
        // cambiaba nada. Se envía SIEMPRE la propiedad propia de la reserva.
        property_id: this.originalPropertyId || this.property_id,

        start_date: startDate,
        end_date: endDate,

        original_start_date: this.originalStartDate || startDate,
        original_end_date: this.originalEndDate || endDate,
        original_property_id: this.originalPropertyId || this.property_id,

        number_of_rooms: lockedQty,

        accommodation_type: lockedRoomType,
        accommodation_type_id: lockedRoomType?.id || null,

        number_of_guests: Number(this.form.number_of_guests) || 1,
        name: this.form.name,
        email: String(this.form.email || "").trim() || null,
        observacion: String(this.form.observacion || "").trim() || null,
        phone: this.normalizePhone(this.form.phone),

        // ✅ ESTE es el valor final (manual o auto), ya no se pisa en submit
        price: Number(this.form.total || this.form.price) || 0,
        promotions: this.form.promotions || [],
        taxes: this.form.taxes || [],

        // Reenvío explícito de la plantilla de confirmación (checkbox del operador).
        resend_confirmation: !!this.resendConfirmation,

        redirect_url: this.redirectUrl || null,
        booking_id: bookingId || null,
      };
    },

    async submit() {
      if (!this.validateBeforeSubmit()) return;

      // ✅ Antes de enviar, valida disponibilidad (y calcula "sugerido" sin pisar manual)
      if (!this.otaLocked) {
        this._lastRequestKey = null;
        await this.refreshAvailabilityAndPrice();
        if (!this.availabilityOk) return;
      }

      if (!this.isEditMode) {
        if (typeof this.onCreateReservation !== "function") {
          toastr.error(this.$t("reservasHc.hotelResModal.missingOnCreate"));
          return;
        }

        // Reserva multi-tipo: líneas extra válidas (tipo elegido, distinto del principal
        // y de otras líneas, cantidad ≥ 1).
        const mainTypeId = this.form.roomType?.id || null;
        const seenTypes = new Set();
        const validExtras = this.extraLines.filter((l) => {
          if (!l.roomTypeId || l.roomTypeId === mainTypeId) return false;
          if (Number(l.quantity) <= 0) return false;
          if (seenTypes.has(l.roomTypeId)) return false;
          seenTypes.add(l.roomTypeId);
          return true;
        });
        /* Última barrera: TODAS las líneas, del mismo hotel.
           El desplegable ya no ofrece otros hoteles, pero esto se comprueba
           igual antes de crear NADA: las líneas se crean con una llamada por
           cabeza y no hay vuelta atrás, así que si la segunda falla la primera
           se queda hecha. Más vale no empezar. */
        const hotelReserva = this.hotelDeLaPrincipal;
        const deOtroHotel = validExtras.filter(
          (l) => this.hotelDe(this.resolveRoomTypeById(l.roomTypeId)) !== hotelReserva,
        );
        if (deOtroHotel.length) {
          toastr.error(this.$t("reservasHc.hotelResModal.sameHotelOnly"));
          return;
        }

        const isMulti = validExtras.length > 0;
        // Código de grupo compartido por todas las líneas (estilo OTA).
        const groupCode = isMulti
          ? `G${Date.now().toString(36).toUpperCase()}${Math.random()
              .toString(36)
              .slice(2, 6)
              .toUpperCase()}`
          : null;

        // Datos compartidos por TODAS las líneas del grupo (huésped, fechas, código).
        // El precio y los huéspedes son GLOBALES de la reserva (no por línea).
        const sharedGuest = {
          name: this.form.name,
          phone: this.normalizePhone(this.form.phone),
          email: String(this.form.email || "").trim() || null,
          observacion: String(this.form.observacion || "").trim() || null,
          start_date: this.form.startDate,
          end_date: this.form.endDate,
          user_id: this.user?.id || null,
          property_id: this.property_id,
          selectShippingDate: this.form.selectShippingDate,
          redirect_url: this.redirectUrl || null,
          group_code: groupCode,
          // No va a la API de reservas: lo usa handleHotelReservation para dejarla en la ficha.
          directorio_cliente_id: this.selectedGuestSuggestionId,
        };

        // Líneas: la principal + las extra (tipo + cantidad).
        const lines = [
          {
            rt: this.form.roomType,
            quantity: Number(this.form.quantity) || 1,
            preset: this.activePresetInstance?.id || null,
            isMain: true,
            price: Number(this.form.total || this.form.price) || 0,
          },
          ...validExtras.map((l) => ({
            rt: this.resolveRoomTypeById(l.roomTypeId),
            quantity: Number(l.quantity) || 1,
            preset: null,
            isMain: false,
            price: Number(l.price) || 0,
          })),
        ];

        // Huéspedes GLOBALES repartidos por capacidad (llena cada habitación hasta su
        // tope; la última recibe el resto). El PRECIO va POR LÍNEA (cada tipo el suyo).
        const totalGuests = Number(this.form.number_of_guests) || 1;
        let remaining = totalGuests;

        const payloads = lines.map((ln, i) => {
          const cap = (Number(ln.rt?.capacity) || 1) * (Number(ln.quantity) || 1);
          let g = i === lines.length - 1 ? remaining : Math.min(remaining, cap);
          g = Math.max(1, g);
          remaining = Math.max(0, remaining - g);
          return {
            ...sharedGuest,
            preferred_room_instance_id: ln.preset,
            accommodation_type: ln.rt,
            quantity: ln.quantity,
            number_of_guests: g,
            price: Math.round((Number(ln.price) || 0) * 100) / 100,
            promotions: ln.isMain ? this.form.promotions || [] : [],
            taxes: ln.isMain ? this.form.taxes || [] : [],
          };
        });
        const payloadCreate = payloads[0];

        this.loaderLocal = true;
        try {
          // 1) Pre-validar la disponibilidad de TODAS las líneas ANTES de crear ninguna,
          //    para no dejar un grupo a medias si una falla.
          for (const p of payloads) {
            const { data: chk } = await this.http.post(
              "/validate/hotel-availability",
              {
                accommodation_type: p.accommodation_type,
                quantity: p.quantity,
                start_date: p.start_date,
                end_date: p.end_date,
              },
              { validateStatus: (s) => s < 500 },
            );
            const ok =
              chk?.available &&
              !(
                typeof chk.available_units === "number" &&
                chk.available_units < p.quantity
              );
            if (!ok) {
              toastr.warning(
                this.$t("reservasHc.hotelResModal.notEnoughAvailInType", {
                  name: p.accommodation_type?.name || this.$t("reservasHc.hotelResModal.aRoomType"),
                }),
              );
              return;
            }
          }

          /* 2) Crear todas las líneas (comparten el código de grupo).

             Cada una es una llamada aparte y NO hay vuelta atrás: si la tercera
             falla, las dos primeras se quedan creadas. Antes eso se iba en un
             toast de error genérico y el operador no sabía si tenía media
             reserva hecha o ninguna. Ahora se dice con números. */
          let allOk = true;
          let creadas = 0;
          for (const p of payloads) {
            const created = await this.onCreateReservation(p);
            if (!created) {
              allOk = false;
              break;
            }
            creadas++;
          }
          if (!allOk && creadas > 0) {
            toastr.warning(
              this.$t("reservasHc.hotelResModal.partiallyCreated", {
                ok: creadas,
                total: payloads.length,
              }),
            );
          }
          if (allOk) {
            this.$emit("created", payloadCreate);
            this.close();
          }
        } finally {
          this.loaderLocal = false;
        }
        return;
      }

      const id = this.bookingId;
      if (!id) return toastr.error(this.$t("reservasHc.hotelResModal.reservationIdNotFound"));

      const payloadUpdate = this.buildUpdatePayload();

      // Reserva COMBINADA: habitaciones marcadas para QUITAR (principal + hermanas). NADA
      // se elimina hasta este guardado y con CONFIRMACIÓN explícita.
      const siblingsToRemove = (this.editGroupLines || []).filter(
        (l) => l.markedForRemoval,
      );
      const removePrimary =
        this.primaryMarkedForRemoval && this.editGroupLines.length > 0;

      // No se puede quitar TODAS las habitaciones (para eso se cancela la reserva).
      const survivors =
        (removePrimary ? 0 : 1) +
        (this.editGroupLines || []).filter((l) => !l.markedForRemoval).length;
      if ((siblingsToRemove.length || removePrimary) && survivors < 1) {
        toastr.warning(this.$t("reservasHc.hotelResModal.cannotRemoveAll"));
        return;
      }

      const removalNames = [
        ...(removePrimary
          ? [this.form.roomType?.name || this.$t("reservasHc.hotelResModal.thisRoom")]
          : []),
        ...siblingsToRemove.map((l) => l.unitName),
      ];
      if (removalNames.length) {
        const ok = window.confirm(
          this.$t("reservasHc.hotelResModal.confirmRemoval", { names: removalNames.join(", ") }),
        );
        if (!ok) return;
      }

      this.loaderLocal = true;
      try {
        // Actualiza la línea principal (propaga datos del huésped + fechas al grupo). Si la
        // principal se va a quitar, igual se hace primero para propagar y luego se elimina.
        const { data } = await this.http.put(
          `/update/booking/${id}`,
          payloadUpdate,
        );

        /* 🚨 EL ORDEN IMPORTA, y antes estaba al revés.
           Primero se bajaban cantidades y después se añadía la habitación nueva.
           Si lo de añadir fallaba —el hotel vende por horas, no quedan libres…—
           la bajada YA se había aplicado: la reserva perdía una habitación, salía
           un aviso naranja, y detrás un «Reserva actualizada exitosamente» verde.
           Le pasó a Felipe Rodrigues: se quedó con una Duplex en vez de dos.

           Ahora lo que CREA va primero y lo que DESTRUYE después, y si algo falla
           se para ahí: es preferible quedarse a medias de más que de menos. */
        let huboFallo = null;

        // 1) AÑADIR las habitaciones de OTRO tipo que el operador puso ahora.
        //    Es lo que convierte «2 dobles» en «1 doble + 1 deluxe» sin cancelar ni
        //    rehacer la reserva: la nueva entra como una línea más del mismo código.
        const nuevasLineas = (this.extraLines || []).filter(
          (l) => l.roomTypeId && Number(l.quantity) > 0,
        );
        for (const l of nuevasLineas) {
          try {
            const body = {
              reserva_id: id,
              accommodation_unit_id: Number(l.roomTypeId),
              quantity: Number(l.quantity),
            };
            // El precio solo se manda si el anfitrión lo escribió: sin él lo cotiza el
            // servidor con la tarifa del tipo nuevo. Mover a alguien de habitación no
            // es una venta nueva, así que normalmente se pone el que ya pagaba.
            if (l.priceMode === "manual" && Number(l.price) > 0) {
              body.price = Number(l.price);
            }
            await this.http.post("/booking-group/add-line", body);
          } catch (e) {
            // El servidor explica POR QUÉ no se pudo (el hotel vende por horas, no
            // quedan habitaciones libres, esa tipología no tiene tarifa…). Su mensaje
            // manda sobre el nuestro: un «no se pudo» a secas deja al anfitrión sin
            // saber qué hacer y pensando que el panel está roto.
            const d = e?.response?.data || {};
            const nombre = this.resolveRoomTypeById(l.roomTypeId)?.name || "";
            huboFallo =
              d.message ||
              d.error ||
              this.$t("reservasHc.hotelResModal.couldNotAdd", { name: nombre });
            break;
          }
        }

        /* Si no entró la habitación nueva, NO se toca lo que ya había: el anfitrión
           quería MOVER a alguien, y mover a medias es quitarle la habitación. */
        if (huboFallo) {
          toastr.warning(huboFallo);
          this.loaderLocal = false;
          this.$emit("updated", data?.booking || null);
          return;
        }

        // 2) Cantidades y precios de las hermanas que SE QUEDAN (no marcadas).
        const changedGroupLines = (this.editGroupLines || []).filter(
          (l) =>
            !l.markedForRemoval &&
            (Number(l.quantity) !== Number(l.originalQuantity) || l.priceEdited),
        );
        for (const l of changedGroupLines) {
          try {
            const body = { reserva_id: l.reservaId, quantity: Number(l.quantity) };
            if (l.priceEdited) body.price = Number(l.lineTotal);
            await this.http.post("/booking-group/set-line-quantity", body);
          } catch (e) {
            const d = e?.response?.data || {};
            huboFallo =
              d.message || d.error ||
              this.$t("reservasHc.hotelResModal.couldNotUpdate", { name: l.unitName });
            toastr.warning(huboFallo);
          }
        }

        // 2) Quitar las hermanas marcadas.
        for (const l of siblingsToRemove) {
          try {
            await this.http.post("/booking-group/remove-line", {
              reserva_id: l.reservaId,
            });
          } catch (e) {
            const d = e?.response?.data || {};
            huboFallo =
              d.message || d.error ||
              this.$t("reservasHc.hotelResModal.couldNotRemove", { name: l.unitName });
            toastr.warning(huboFallo);
          }
        }

        // 3) Quitar la PRINCIPAL si se marcó (queda ≥1 habitación viva por la guarda; el
        //    backend re-asigna check-in/pago/chat a la nueva principal del grupo).
        if (removePrimary) {
          try {
            await this.http.post("/booking-group/remove-line", { reserva_id: id });
          } catch (e) {
            huboFallo = this.$t("reservasHc.hotelResModal.couldNotRemovePrimary");
            toastr.warning(huboFallo);
          }
        }

        /* Antes esto salía SIEMPRE, incluso detrás de un aviso de que algo no se
           había podido hacer: dos mensajes contradictorios a la vez. */
        if (huboFallo) {
          toastr.warning(this.$t("reservasHc.hotelResModal.updatedPartially"));
        } else {
          toastr.success(this.$t("reservasHc.hotelResModal.reservationUpdated"));
        }
        this.$emit("updated", data?.booking || null);

        if (removePrimary) {
          // La PRINCIPAL (la reserva que se estaba viendo) se quitó: avisar al panel para
          // que cambie a la habitación que QUEDA (nueva primaria = menor id vivo del grupo).
          const survivingIds = (this.editGroupLines || [])
            .filter((l) => !l.markedForRemoval)
            .map((l) => Number(l.reservaId));
          const fallbackId = survivingIds.length ? Math.min(...survivingIds) : null;
          this.$emit("changed", { removedId: Number(id), fallbackId });
        } else if (this.$store?.state) {
          // Refresh normal del padre (SOLO EDICIÓN).
          this.$store.state.updateComplete = true;
        }
        this.close();
      } catch (err) {
        const msg =
          err?.response?.data?.error || this.$t("reservasHc.hotelResModal.couldNotUpdateReservation");
        toastr.error(msg);
      } finally {
        this.loaderLocal = false;
      }
    },
  },
};
</script>

<!-- CSS base de vue-multiselect (selector con buscador) -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
/* Modal de reserva (hotel). Comparte el sistema visual .rm-* definido en
   RerservationModal.vue (CSS global cargado desde App.vue). Aquí solo van los
   estilos propios del flujo de hotel: campos requeridos, reserva multi-tipo,
   chip de habitación preseleccionada y el encabezado del precio. */

/* En apartamento flota junto a las fechas; aquí las fechas no tienen ese contenedor
   y se iba a la esquina del cajón. Va en su sitio, debajo de las fechas. */
.rm-dialog--hotel .rm-nights {
  position: static;
  width: fit-content;
}

.rm-req { color: #ef4444; font-weight: 700; }
.rm-label__hint { font-weight: 500; color: #94a3b8; font-size: 12px; }

.rm-textarea {
  height: auto;
  min-height: 84px;
  padding: 11px 14px;
  line-height: 1.4;
  resize: vertical;
}

/* Encabezado del precio (label + "usar precio automático") */
.rm-price-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;

  .rm-label--price { margin-bottom: 0; }
}
.rm-autoprice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #4a5bd4;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover { background: #f3f5fd; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* Reserva multi-tipo: líneas de tipos adicionales */
.extra-type-line {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 10px;
}
.etl-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  small { font-size: 12px; color: #475569; font-weight: 600; }
}
.etl-field--type { flex: 1 1 auto; min-width: 0; }
.etl-field--qty { width: 168px; flex: 0 0 auto; }
.etl-avail { color: #4a5bd4; font-weight: 700; }
.etl-checking { color: #94a3b8; }
.extra-type-remove {
  flex: 0 0 auto;
  border: none;
  background: #fef2f2;
  color: #ef4444;
  border-radius: 10px;
  width: 46px;
  height: 46px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #fee2e2; }
}
.extra-type-add {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px dashed #c7d2fe;
  background: #f8faff;
  color: #4a5bd4;
  border-radius: 11px;
  padding: 10px 15px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #eef2ff; }
}

/* Chip de habitación preseleccionada (drag en la fila del calendario) */
.preset-room-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
  padding: 9px 12px;
  background: #f3f5fd;
  border: 1px solid #d8def7;
  border-radius: 11px;
  font-size: 12.5px;
  color: #4f5d9b;
  line-height: 1.35;

  i { color: #4a5bd4; font-size: 13px; flex-shrink: 0; }
  strong { color: #4a5bd4; }
  small { display: block; font-size: 11px; color: #8a93ad; }
}

/* ── Desglose de precios por habitación (reserva multi-tipo) ── */
.rm-bd {
  border: 1px solid #e6e9f4;
  border-radius: 14px;
  background: #fbfcff;
  padding: 4px 14px 10px;
  box-shadow: 0 4px 20px rgba(31, 41, 78, 0.05);
}
.rm-bd__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 2px;
  border-bottom: 1px solid #eceffa;

  .rm-label--price { margin-bottom: 0; }
}
.rm-bd__nights {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4a5bd4;
  white-space: nowrap;

  i { font-size: 12px; }
}
.rm-bd__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 2px;
  border-bottom: 1px solid #f1f3fb;
}
.rm-bd__info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.rm-bd__ic {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef1fd;
  color: #4a5bd4;

  i { font-size: 14px; }
}
.rm-bd__txt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rm-bd__name {
  font-size: 13.5px;
  font-weight: 700;
  color: #1f2740;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rm-bd__meta {
  font-size: 11.5px;
  color: #8a93ad;
  font-weight: 500;
}
.rm-bd__calc { color: #4a5bd4; font-weight: 600; }
.rm-bd__pay {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
}
.rm-bd__auto {
  border: none;
  background: #eef1fd;
  color: #4a5bd4;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: #e0e6fb; }
  i { font-size: 12.5px; }
}
/* Input de precio compacto para el desglose */
.rm-price--sm {
  width: 138px;

  .rm-price__val { font-size: 14px; }
}
/* Total sumado */
.rm-bd__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 2px 3px;
}
.rm-bd__total-lb {
  font-size: 12.5px;
  font-weight: 700;
  color: #64708f;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.rm-bd__total-val {
  font-size: 20px;
  font-weight: 800;
  color: #1f2740;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

/* ══ Habitaciones y precio: filas Tipo · Cantidad · Precio/noche ══ */
.rm-rooms { display: flex; flex-direction: column; gap: 8px; }
.rm-rooms__extras { display: flex; flex-direction: column; gap: 8px; position: relative; }

/* Alta/baja de filas extra (ease-out, sin bounce) + reordenamiento suave (FLIP) */
.rm-row-enter-active,
.rm-row-leave-active { transition: opacity .3s cubic-bezier(.22, 1, .36, 1), transform .3s cubic-bezier(.22, 1, .36, 1); }
.rm-row-enter { opacity: 0; transform: translateY(-8px); }
.rm-row-leave-to { opacity: 0; transform: translateY(-8px); }
.rm-row-leave-active { position: absolute; left: 0; right: 0; }
.rm-row-move { transition: transform .3s cubic-bezier(.22, 1, .36, 1); }

/* Pulso breve del Total al recalcular */
.rm-rooms__total-val.is-bump { animation: rm-bump .34s cubic-bezier(.22, 1, .36, 1); }
@keyframes rm-bump {
  0% { transform: scale(1); }
  38% { transform: scale(1.05); color: #4a5bd4; }
  100% { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .rm-row-enter-active,
  .rm-row-leave-active,
  .rm-row-move { transition: none; }
  .rm-rooms__total-val.is-bump { animation: none; }
}

.rm-rooms__cols {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 2px 2px;
}
.rmc {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}
.rmc--type { flex: 1 1 auto; min-width: 0; }
.rmc--qty { width: 74px; flex: 0 0 auto; text-align: center; }
.rmc--price { width: 150px; flex: 0 0 auto; }
.rmc--x { width: 34px; flex: 0 0 auto; }

.rm-room { display: flex; align-items: center; gap: 10px; }
.rm-room__type { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.rm-room__qty { width: 74px; flex: 0 0 auto; position: relative; }
.rm-room__price {
  width: 150px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 11px;
  padding: 0 10px;
  transition: border-color 0.15s;
}
.rm-room__price:focus-within { border-color: #4a5bd4; }
.rm-cur { font-size: 13px; font-weight: 700; color: #64708f; flex: 0 0 auto; }
.rm-input--c { width: 100%; }
.rm-room__qty .rm-input--c { text-align: center; font-weight: 700; }
.rm-room__price .rm-input--c {
  border: none !important;
  background: transparent !important;
  padding: 10px 0 !important;
  height: auto !important;
  box-shadow: none !important;
  font-weight: 700;
  color: #1f2740;
}
.rm-room__auto {
  flex: 0 0 auto;
  border: none;
  background: #eef1fd;
  color: #4a5bd4;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  i { font-size: 11px; }
  &:hover { background: #e0e6fb; }
}
.rm-room__mini {
  position: absolute;
  left: 2px;
  top: 100%;
  margin-top: 2px;
  font-size: 10.5px;
  color: #94a3b8;
  white-space: nowrap;
}
.rm-room--extra { margin-top: 0; }

/* Fila principal: la etiqueta de la habitación del calendario va ARRIBA del input */
.rm-room--main { align-items: flex-end; }
.rm-room__preset {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 0 2px 1px;
  font-size: 10.5px;
  font-weight: 700;
  color: #4a5bd4;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  i { font-size: 11px; }
  strong { font-weight: 800; }
}
.rm-room__preset-sub { color: #9aa3b8; font-weight: 600; }
.rm-preset-enter-active { transition: opacity 0.3s ease; }
.rm-preset-enter { opacity: 0; }

/* Nota de disponibilidad por fila extra (EN FLUJO → no se solapa con "Añadir…") */
.rm-xrow { display: flex; flex-direction: column; gap: 3px; }
.rm-room__note {
  margin: 0;
  padding: 0 46px 0 2px;
  font-size: 11px;
  font-weight: 600;
  color: #9aa3b8;
  text-align: right;
}
.rm-note-enter-active,
.rm-note-leave-active { transition: opacity 0.24s cubic-bezier(0.22, 1, 0.36, 1), transform 0.24s cubic-bezier(0.22, 1, 0.36, 1); }
.rm-note-enter,
.rm-note-leave-to { opacity: 0; transform: translateY(-4px); }

@media (prefers-reduced-motion: reduce) {
  .rm-note-enter-active,
  .rm-note-leave-active,
  .rm-preset-enter-active { transition: none; }
}
.rm-room__x { width: 34px; flex: 0 0 auto; }
.rm-room__x--btn {
  height: 42px;
  border: none;
  background: #fef2f2;
  color: #ef4444;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: #fee2e2; }
}

.rm-rooms__total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding: 12px 14px;
  background: #f7f8fd;
  border: 1px solid #e6e9f4;
  border-radius: 12px;
}
.rm-rooms__total-lb {
  font-size: 13px;
  font-weight: 700;
  color: #64708f;

  small { font-weight: 500; color: #94a3b8; }
}
.rm-rooms__total-val {
  font-size: 20px;
  font-weight: 800;
  color: #1f2740;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

/* Modal de HOTEL algo más ancho (para las 3 columnas). NO afecta al apartamento. */
.rm-dialog.rm-dialog--hotel { max-width: 620px; }
.rm-dialog--mobile.rm-dialog--hotel { max-width: 100%; }
@media (max-width: 600px) {
  .rm-dialog.rm-dialog--hotel { max-width: 100%; }
  /* En móvil las filas se apilan para no romperse */
  .rm-rooms__cols { display: none; }
  .rm-room { flex-wrap: wrap; }
  .rm-room__type { flex: 1 1 100%; }
  .rm-room__qty { flex: 1 1 40%; width: auto; }
  .rm-room__price { flex: 1 1 50%; width: auto; }
  .rm-room__x { width: auto; }
}
</style>
